import React, { useRef, useState } from 'react';
import { IconButton } from '@mui/material';
import ReactPlayer from 'react-player/lazy';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import iconBack from '../../assets/button_back.svg';
import iconForward from '../../assets/button_forward.svg';
import iconPlay from '../../assets/button_play.svg';
import iconPause from '../../assets/button_pause.svg';

type VideoGalleryProps = {
    items?: {
        video?: {
            filename?: string;
        };
        preview_image?: {
            filename?: string;
        };
        show_preview_image?: boolean;
    }[];
};

export const VideoGallery: React.FC<VideoGalleryProps> = ({
    items,
}) => {
    const swiperRef = useRef<SwiperType>();
    const reactPlayerRef = useRef<ReactPlayer>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [showPreviewImages, setShowPreviewImages] = useState<boolean[]>(items?.map(item => item.show_preview_image ?? false) || []);

    const settings = {
        loop: true,
        speed: 500,
        slidesPerView: 1,
    };

    const handlePrev = () => {
        if (swiperRef.current) {
            setIsPlaying(false);
            swiperRef.current.slidePrev();
        }
    };

    const handleNext = () => {
        if (swiperRef.current) {
            setIsPlaying(false);
            swiperRef.current.slideNext();
        }
    };

    return (
        <div className='w-full h-full bg-white pt-16 tablet:pt-24 relative'>
            <div className='bg-hylo-hellblau h-[50%] w-full absolute top-[17%] tablet:top-[18%] desktop:top-[25%]'></div>
            <Swiper
                onSwiper={(swiper) => {
                    swiperRef.current = swiper;
                }}
                onSlideChange={(swiper) => {
                    setActiveIndex(swiper.realIndex);
                }}
                loopAddBlankSlides={false}
                className="mySwiper"
                {...settings}
            >
                {items?.map((item, index) => (
                    <SwiperSlide key={index}>
                        <div
                            className='bg-transparent w-full h-auto overflow-hidden flex justify-center items-center'
                            onClick={(e) => {
                                if (showPreviewImages[index]) {
                                    setShowPreviewImages(prev => {
                                        const newState = [...prev];
                                        newState[index] = false;
                                        return newState;
                                    });
                                    setIsPlaying(true);
                                } else {
                                    setIsPlaying(!isPlaying);
                                }
                            }}
                        >
                            <ReactPlayer
                                ref={reactPlayerRef}
                                url={item.video?.filename}
                                playing={isPlaying && swiperRef.current?.realIndex === index}
                                controls={false}
                                loop={true}
                                muted={false}
                                volume={1}
                                playsinline={true}
                                width='90%'
                                height='90%'
                                className='tablet:!w-[70%] tablet:!h-[70%] cursor-pointer'
                                light={showPreviewImages[index] && item.preview_image?.filename ? <img src={item.preview_image?.filename} alt='HYLO®' /> : false}
                                playIcon={<div className='hidden'></div>}
                            />
                        </div>
                    </SwiperSlide>
                ))}
                <div className='bg-transparent flex justify-center items-center gap-6 phablet:gap-10 p-12 tablet:p-16'>
                    {/* invisible element to center the control buttons */}
                    <div className='text-gray-600 style-16-mobile tablet:style-16 mt-[-5px] invisible'>
                        {items ? `${activeIndex + 1}/${items.length}` : '0/0'}
                    </div>
                    <IconButton onClick={handlePrev} disableRipple={true}>
                        <img
                            src={iconBack}
                            alt='Previous'
                            className='h-[22px] w-[22px] tablet:h-[36px] tablet:w-[36px]'
                        />
                    </IconButton>
                    <IconButton
                        disableRipple={true}
                        onClick={() => {
                            const realIndex = swiperRef.current?.realIndex;
                            if (realIndex !== undefined) {
                                if (reactPlayerRef.current?.getInternalPlayer()) {
                                    setIsPlaying((prev) => !prev);
                                    // Force exit light mode when playing
                                    setShowPreviewImages(prev => {
                                        const newState = [...prev];
                                        newState[realIndex] = false;
                                        return newState;
                                    });
                                }
                            }
                        }}
                    >
                        <img
                            src={isPlaying ? iconPause : iconPlay}
                            alt='Play/Pause'
                            className='h-[22px] w-[22px] tablet:h-[36px] tablet:w-[36px]'
                        />
                    </IconButton>
                    <IconButton onClick={handleNext} disableRipple={true}>
                        <img
                            src={iconForward}
                            alt='Next'
                            className='h-[22px] w-[22px] tablet:h-[36px] tablet:w-[36px]'
                        />
                    </IconButton>
                    <div className='text-gray-600 style-16-mobile tablet:style-16 text-[1.8rem] mt-[-5px] tabular-nums'>
                        {items ? `${activeIndex + 1}/${items.length}` : '0/0'}
                    </div>
                </div>
            </Swiper>
        </div >
    );
};